import { useState } from 'react'
import { Button, Icon, Menu, MenuRow, Pill, PillCluster, toastError } from 'admination-react'
import { resetMe, setActiveOrganisation, setActiveVariant } from '@/core/data/session'
import { waitForSettingsWrites } from '@/core/api/settingsWrites'
import { assertWriteIdentity, captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { useApp } from '@/core/state/appStore'
import { signOut } from '@/core/api/auth'
import { friendlyErrorMessage } from '@/core/lib/dbError'

export const OPEN_ORG_SETTINGS_PARAM = 'openOrgSettings'

export function UserMenu({ onOpenSettings, onOpenMeineEinstellungen }: {
  onOpenSettings?: () => void
  onOpenMeineEinstellungen?: () => void
}) {
  const { identity } = useApp()
  const name = identity ? [identity.vorname, identity.nachname].filter(Boolean).join(' ') || identity.email : 'Konto'
  return <PillCluster className="pill-cluster--identity" aria-label="Arbeitsbereich">
    {identity && <Pill><Button onClick={onOpenSettings} aria-label={`Organisationseinstellungen: ${identity.organisationName}`}>
      <Icon name="i-building" /><span className="bar-title">{identity.organisationName}</span>
    </Button></Pill>}
    <Pill><Button disabled={!identity} onClick={onOpenMeineEinstellungen} aria-label={`Meine Einstellungen: ${name}`}>
      <Icon name="i-person" /><span className="bar-title">{name}</span>
    </Button></Pill>
  </PillCluster>
}

export function AccountLabel({ organisation = false }: { organisation?: boolean }) {
  const { identity } = useApp()
  const name = identity && (organisation ? identity.organisationName : [identity.vorname, identity.nachname].filter(Boolean).join(' ') || identity.email)
  return name ? <Pill><span className="btn btn--static"><Icon name={organisation ? 'i-building' : 'i-person'} /><span className="bar-title">{name}</span></span></Pill> : null
}

export function OrganisationSwitcher() {
  const { identity, state, dispatch } = useApp()
  const busy = state.workspaceSwitching
  async function selectOrganisation(id: string) {
    if (busy || !identity || id === identity.organisationId) return
    const operationIdentity = captureWriteIdentity()
    dispatch({ type: 'SET_CONTROL', key: 'workspaceSwitching', value: true })
    ;(document.activeElement as HTMLElement | null)?.blur()
    try {
      await waitForSettingsWrites()
      assertWriteIdentity(operationIdentity)
      const switched = await setActiveOrganisation(id)
      assertWriteIdentity(switched)
      window.location.reload()
    } catch (error) {
      if (!isWriteIdentityCurrent(operationIdentity)) return
      dispatch({ type: 'SET_CONTROL', key: 'workspaceSwitching', value: false })
      toastError(friendlyErrorMessage(error, 'Organisation wechseln fehlgeschlagen.'))
    }
  }
  async function selectVariant(key: string) {
    if (busy || !identity || key === identity.variant || !identity.variantsOffered.includes(key)) return
    const operationIdentity = captureWriteIdentity()
    dispatch({ type: 'SET_CONTROL', key: 'workspaceSwitching', value: true })
    ;(document.activeElement as HTMLElement | null)?.blur()
    try {
      await waitForSettingsWrites()
      assertWriteIdentity(operationIdentity)
      const switched = await setActiveVariant(identity.organisationId, key)
      assertWriteIdentity(switched)
      window.location.reload()
    } catch (error) {
      if (!isWriteIdentityCurrent(operationIdentity)) return
      dispatch({ type: 'SET_CONTROL', key: 'workspaceSwitching', value: false })
      toastError(friendlyErrorMessage(error, 'Maßnahmen-Art wechseln fehlgeschlagen.'))
    }
  }
  const variants = identity?.variants.filter((variant) => identity.variantsOffered.includes(variant.key)).map((variant) => ({ ...variant, name: variant.key === 'azav' ? 'AVGS' : variant.name })) ?? []
  const active = variants.find((variant) => variant.key === identity?.variant)
  const label = <><Icon name="i-building" />Organisation wechseln</>
  return <>
    {variants.length > 1 && <Pill><Menu presentation="pill" trigger={<><Icon name="i-massnahme" />{active?.name}</>} triggerButtonProps={{ disabled: busy }}>
      {variants.map((variant) => <MenuRow key={variant.key} label={variant.name} icon={variant.key === 'azav' ? 'i-voucher' : 'i-clipboard'}
        isSelected={variant.key === identity?.variant} disabled={busy}
        onClick={() => { void selectVariant(variant.key) }} />)}
    </Menu></Pill>}
    {!identity || identity.memberships.length < 2 ? <Pill><Button variant="danger" disabled>{label}</Button></Pill>
      : <Pill><Menu presentation="pill" trigger={label} triggerButtonProps={{ variant: 'danger', disabled: busy }}>
        {identity.memberships.map((org) => <MenuRow key={org.organisationId} label={org.organisationName} icon="i-building"
          isSelected={org.organisationId === identity.organisationId} disabled={busy}
          onClick={() => { void selectOrganisation(org.organisationId) }} />)}
      </Menu></Pill>}
  </>
}

export function AccountSignOut() {
  const [busy, setBusy] = useState(false)
  async function leave() {
    if (busy) return
    const identity = captureWriteIdentity()
    setBusy(true)
    try {
      const signedOut = await signOut()
      assertWriteIdentity(signedOut)
      resetMe()
      window.location.assign('/')
    } catch (error) {
      if (!isWriteIdentityCurrent(identity)) return
      setBusy(false)
      toastError(friendlyErrorMessage(error, 'Abmelden fehlgeschlagen.'))
    }
  }
  return <Pill><Button variant="danger" disabled={busy} onClick={() => { void leave() }}><Icon name="i-logout" />Abmelden</Button></Pill>
}
